import type { Attachment } from '@nexo/types';
import { cn } from '@nexo/ui';
import { Eye } from 'lucide-react';
import { formatBytes } from '../../lib/format';
import { FileIcon } from '../files/FileIcon';
import { viewer } from '../viewer/viewerStore';
import { useTranslation } from 'react-i18next';

export function MessageAttachments({ attachments }: { attachments: Attachment[] }) {
  const { t } = useTranslation();
  if (attachments.length === 0) return null;
  const images = attachments.filter((a) => a.isImage);
  const files = attachments.filter((a) => !a.isImage);
  // The viewer shows all of the message's attachments, images first.
  const ordered = [...images, ...files];
  const open = (attachment: Attachment) => viewer.open(ordered, ordered.indexOf(attachment));

  return (
    <div className="mt-2 flex flex-col gap-2">
      {images.length > 0 && (
        <div
          className={cn('grid max-w-xl gap-2', images.length === 1 ? 'grid-cols-1' : 'grid-cols-2')}
        >
          {images.map((image) => (
            <button
              key={image.id}
              type="button"
              onClick={() => open(image)}
              aria-label={t('Ver {{name}}', { name: image.name })}
              className="group overflow-hidden rounded-xl border border-border bg-surface-2"
            >
              <img
                src={image.url}
                alt={image.name}
                loading="lazy"
                className={cn(
                  'w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]',
                  images.length === 1 ? 'max-h-80 object-contain' : 'aspect-[4/3]',
                )}
              />
            </button>
          ))}
        </div>
      )}
      {files.map((file) => (
        <button
          key={file.id}
          type="button"
          onClick={() => open(file)}
          className="group flex max-w-sm items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2.5 text-left shadow-panel transition-colors hover:border-border-strong"
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
            <FileIcon contentType={file.contentType} name={file.name} className="size-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{file.name}</span>
            <span className="block text-xs text-subtle">
              {formatBytes(file.size)}
              {file.previewUrl ? t(' · Ver') : t(' · Descargar')}
            </span>
          </span>
          <Eye className="size-4 text-subtle group-hover:text-text" />
        </button>
      ))}
    </div>
  );
}
